import type { PhaseCatalogDto, SiteProgressDto } from '@app/contracts';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  PHASE_2_NEEDS_WAIT_DOCUMENT_RIGHTS_TASK,
  PHASE_2_NEEDS_WAIT_GROUP_RULES_TASK,
} from '../constants/phase-task-codes.js';

interface Props {
  phase: PhaseCatalogDto;
  progress: SiteProgressDto;
  catalogPhaseOrder: (code: string) => number;
  onAdvanceUser: () => void;
}

export function PhaseTaskList({ phase, progress, catalogPhaseOrder, onAdvanceUser }: Props) {
  const [openInfo, setOpenInfo] = useState<string | null>(null);
  const sitePhaseOrder = catalogPhaseOrder(progress.sitePhaseCode);
  const userPhaseOrder = catalogPhaseOrder(progress.userPhaseCode);
  const phaseOrder = phase.order;

  const phaseLocked = phaseOrder > sitePhaseOrder;
  const phaseIsCurrentUser = phaseOrder === userPhaseOrder;

  const completedSet = new Set(progress.completedTaskCodes);

  return (
    <div className="space-y-5">
      <div>
        <p className="text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-emerald-800/95">
          Phase {phase.order}
        </p>
        <h3 className="mt-2 text-xl font-semibold tracking-tight text-slate-900">{phase.label}</h3>
        {phase.description && (
          <p className="mt-2 text-[0.92rem] leading-relaxed text-slate-600">{phase.description}</p>
        )}
      </div>

      {phaseLocked && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[0.9rem] text-amber-900">
          Cette phase est verrouillée jusqu'à ce que le site progresse jusqu'ici.
        </div>
      )}

      <ul className="space-y-3">
        {phase.tasks.map((task) => {
          const done = completedSet.has(task.code);
          const openRightsFromJourney = task.code === PHASE_2_NEEDS_WAIT_DOCUMENT_RIGHTS_TASK;
          const openGroupsTabFromJourney = task.code === PHASE_2_NEEDS_WAIT_GROUP_RULES_TASK;
          return (
            <li
              key={task.code}
              className={`rounded-2xl border bg-white/80 shadow-sm transition-colors ${
                phaseLocked
                  ? 'border-slate-200'
                  : 'border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40'
              }`}
            >
              <Link
                to={task.path}
                {...(openRightsFromJourney
                  ? { search: { openDocumentRights: true } }
                  : openGroupsTabFromJourney
                    ? { search: { tab: 'groups' } }
                    : {})}
                className={`block p-4 ${phaseLocked ? 'pointer-events-none' : ''}`}
                tabIndex={phaseLocked ? -1 : 0}
              >
                <div className="flex items-start gap-3">
                  <span
                    aria-hidden
                    className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 ${
                      done
                        ? 'border-emerald-500 bg-emerald-500'
                        : phaseLocked
                          ? 'border-slate-200 bg-slate-50'
                          : 'border-slate-300 bg-white'
                    }`}
                  >
                    {done && (
                      <svg
                        className="h-3 w-3 text-white"
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2.5}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                      >
                        <title>Tâche terminée</title>
                        <path d="M2 6l3 3 5-5" />
                      </svg>
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`text-sm font-semibold tracking-tight ${
                          done ? 'text-slate-500 line-through' : 'text-slate-900'
                        }`}
                      >
                        {task.label}
                      </span>
                      {task.mandatory ? (
                        <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[0.65rem] font-semibold uppercase text-rose-700 ring-1 ring-rose-500/30">
                          Obligatoire
                        </span>
                      ) : (
                        <span className="rounded-full bg-slate-50 px-2 py-0.5 text-[0.65rem] font-semibold uppercase text-slate-600 ring-1 ring-slate-300/60">
                          Recommandée
                        </span>
                      )}
                      <button
                        type="button"
                        className="text-emerald-700 hover:text-emerald-900"
                        onClick={(e) => {
                          e.preventDefault();
                          setOpenInfo((c) => (c === task.code ? null : task.code));
                        }}
                        title="Plus d'info"
                      >
                        <span aria-hidden>ⓘ</span>
                        <span className="sr-only">Information</span>
                      </button>
                    </div>
                    {openInfo === task.code && (
                      <p className="mt-2 rounded-xl bg-slate-50 px-3 py-2 text-[0.85rem] leading-relaxed text-slate-700">
                        {task.info}
                      </p>
                    )}
                    {!phaseLocked && (
                      <p className="mt-2 flex items-center gap-1 text-[0.78rem] text-emerald-700">
                        <svg
                          className="h-3.5 w-3.5 flex-shrink-0"
                          viewBox="0 0 16 16"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <title>Lien vers la page</title>
                          <path d="M3 8h10M9 4l4 4-4 4" />
                        </svg>
                        {task.path}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>

      {phaseIsCurrentUser && !phaseLocked && (
        <div className="rounded-2xl border border-emerald-200 bg-white/70 p-4">
          <p className="text-sm font-semibold tracking-tight text-slate-900">
            Passer à la phase suivante
          </p>
          <p className="mt-1 text-[0.85rem] text-slate-600">
            Vous pouvez avancer dès que vous avez pris connaissance des éléments de cette phase.
            Vous ne pouvez pas dépasser la phase actuelle du site.
          </p>
          <button
            type="button"
            className="btn-primary mt-3"
            disabled={userPhaseOrder >= sitePhaseOrder}
            onClick={onAdvanceUser}
          >
            Passer à la phase suivante
          </button>
        </div>
      )}
    </div>
  );
}
